import type { GetServerSideProps } from 'next';
import type { SearchPageProps } from './SearchPage';

import { ApolloError } from '@apollo/client';
import { GraphQLError } from 'graphql';

import { withSSROptimizely } from '@zoopla/optimizely/server';
import { fetchGraphql } from '@zoopla/fetch-graphql';
import { setupLogger } from '@zoopla/logger';

import {
    LISTINGS_QUERY_STR,
    type ApiSearchByUrlQuery,
    type GetListingDataQuery,
    type GetListingDataV2Query
} from '@zoopla/listing-search-queries';
import {
    getServerRenderedData,
    getServerRenderedDataV2
} from './get-server-rendered-data';
import {
    ServerData,
    generateInitialSearchState,
    SearchResultMeta,
    SearchType,
    SearchContextType
} from '@zoopla/listing-search-context';

import { search } from '@zoopla/data/faster-search';
import { hasPathMatch } from './hasPathMatch';

type Gallery = [caption: string | null, uri: string][];
type SearchData = (GetListingDataQuery | GetListingDataV2Query) &
    Pick<ApiSearchByUrlQuery, 'seo'>;

const travelTimeSearches = /(?=.*[\w]*transport_type=[^&\s]*).*$/;

export const getServerSidePropsFor = (
    searchType: SearchType,
    directQuery = false
) => {
    const _getServerSideProps: GetServerSideProps<SearchPageProps> = async (
        ctx
    ) => {
        const logger = setupLogger({
            page: 'listing-search-page',
            custodian: 'search',
            searchType
        });

        const { res, resolvedUrl, query } = ctx;
        const path = removeEverythingAfterHash(resolvedUrl);
        const initialSearchState = generateInitialSearchState({
            searchType
        });

        try {
            let data: SearchData | undefined;
            let errors: GraphQLError[] | Error[] | undefined;

            const isTravelTime = hasPathMatch({
                context: ctx,
                includedPaths: [travelTimeSearches],
                excludedPaths: []
            });

            if (!isTravelTime) {
                const result = await search(path);
                data = result.data;
                errors = result.errors;

                const { seo } = data ?? {};
                const shouldRedirect =
                    seo && 'redirection' in seo && seo.redirection?.uri;

                if (errors && !shouldRedirect) {
                    throw errors;
                }
            } else {
                const result = await fetchGraphql<SearchData>(
                    LISTINGS_QUERY_STR,
                    { path }
                );

                data = result.data;
                errors = result.errors as GraphQLError[];
            }

            const { seo } = data ?? {};
            const shouldRedirect =
                seo && 'redirection' in seo && seo.redirection?.uri;

            if (errors && !shouldRedirect) {
                // mimic Apollo throw behavior
                throw new ApolloError({
                    graphQLErrors: errors as GraphQLError[]
                });
            }

            if (shouldRedirect) {
                return {
                    redirect: {
                        destination: seo.redirection?.uri ?? '',
                        permanent: seo.redirection?.code?.toString() === '301'
                    }
                };
            }

            const props = createPropsFromData(data, initialSearchState);

            res.setHeader('Cache-Control', 's-maxage=600');

            return { props };
        } catch (error) {
            if (Array.isArray(error)) {
                for (let i = 0; i < error.length; i++) {
                    if (
                        ['GEO_ERROR', 'AMBIGUOUS_GEO_ERROR'].includes(
                            error[i].message
                        )
                    ) {
                        const section = path?.includes('/for-sale')
                            ? 'for-sale'
                            : 'to-rent';

                        return {
                            redirect: {
                                destination: `/search/?section=${section}&q=${
                                    error[i].cause?.geoId ?? ''
                                }`,
                                permanent: false
                            }
                        };
                    }
                }
            }

            const geoError = getGeoError(error);
            if (geoError) {
                const errLocation =
                    geoError?.extensions?.['response']?.body?.errors[0]
                        ?.validation_errors[0]?.current_value;

                const q = errLocation !== '' ? errLocation : getQ(query['q']);

                const section = path?.includes('/for-sale')
                    ? 'for-sale'
                    : 'to-rent';

                return {
                    redirect: {
                        destination: `/search/?section=${section}&q=${q ?? ''}`,
                        permanent: false
                    }
                };
            }

            const formattedError: SearchPageProps['error'] = {
                message: errorWithMessage(error)
            };

            logger.error({ error: formattedError }, 'Listing search error: ');

            return {
                props: {
                    initialState: initialSearchState,
                    error: formattedError
                }
            };
        }
    };

    return withSSROptimizely.getServerSideProps<SearchPageProps>(
        _getServerSideProps
    );
};

export function createPropsFromData(
    data: SearchData | undefined,
    initialSearchState: SearchContextType
): SearchPageProps {
    let breadcrumbs = data?.breadcrumbs ?? [];
    let searchResultsMeta: SearchResultMeta | undefined;
    let serverRenderedData:
        | ReturnType<typeof getServerRenderedDataV2>
        | ReturnType<typeof getServerRenderedData>
        | null = null;

    if (data && 'searchResults' in data) {
        if ('seo' in data && data.seo && 'errorCode' in data.seo) {
            throw new ApolloError({
                graphQLErrors: [
                    new GraphQLError(JSON.stringify(data.seo, null, '\t'))
                ]
            });
        } else if (data.seo && 'onPage' in data.seo) {
            const { analyticsEcommerce, analyticsTaxonomy } =
                data.searchResults || {};
            const { info, onPage, internalLinks } = data.seo || {};

            breadcrumbs = data.seo.breadcrumbs ?? [];

            searchResultsMeta = nonEmpty({
                analyticsTaxonomy,
                analyticsEcommerce,
                metaInfo: {
                    title: onPage?.title,
                    canonicalUri: info?.canonicalUri,
                    description: onPage?.description
                },
                seoBlurb: onPage?.description,
                title: onPage?.heading
            });

            const currentlyAvailable = internalLinks?.currentlyAvailable;
            const townsAndCities = internalLinks?.townsAndCities;

            const seoAccordions = [];

            if (currentlyAvailable) {
                seoAccordions.push({
                    ...currentlyAvailable,
                    name: 'currentlyAvailable',
                    expanded: true
                });
            }

            if (townsAndCities) {
                seoAccordions.push({
                    ...townsAndCities,
                    name: 'townsAndCities',
                    expanded: true
                });
            }

            serverRenderedData = getServerRenderedData(
                {
                    breadcrumbs,
                    seoAccordions: nonEmpty(seoAccordions),
                    searchResults: nonEmpty(data.searchResults),
                    redirects: nonEmpty(data.redirects) ?? []
                },
                initialSearchState
            );
        } else {
            searchResultsMeta = extractSearchResultsMeta(
                nonEmpty(data.searchResults)
            );

            serverRenderedData = getServerRenderedData(
                {
                    breadcrumbs, // unused?
                    seoAccordions: nonEmpty(data.seoAccordions) ?? [],
                    searchResults: nonEmpty(data.searchResults),
                    redirects: nonEmpty(data.redirects) ?? []
                },
                initialSearchState
            );
        }
    }

    if (!serverRenderedData || !searchResultsMeta) {
        throw new Error('no data');
    }

    return {
        initialState:
            serverRenderedData.searchContextData || initialSearchState,
        regularListingsFormatted: compressListings(
            serverRenderedData.regularListingsFormatted
        ),
        extendedListingsFormatted: compressListings(
            serverRenderedData.extendedListingsFormatted
        ),
        featuredProperties: serverRenderedData.featuredProperties.map((fp) => {
            if (typeof fp === 'string') return fp;
            // we don't show Featured Property galleries
            const { otherPropertyImages, ...rest } = fp;
            return rest;
        }),
        transactionType: serverRenderedData.transactionType,
        searchResultsMeta,
        breadcrumbs,
        links: serverRenderedData.links
    };
}

/**
 * With data for hundreds of images on a single LSRP page the JSON markup quickly
 * adds up to a very heavy page. Replace the listing galleries image list with
 * a slimmed down tuple array
 */
function compressListings<
    T extends {
        otherPropertyImages?:
            | ({ large?: string | null; caption?: string | null } | null)[]
            | null;
    } = Record<string, unknown>
>(listings: T[]) {
    return listings.map((l) => {
        const { otherPropertyImages, ...rest } = l;

        const gallery = otherPropertyImages?.reduce<Gallery>((acc, curr) => {
            if (curr && curr.large) {
                const src = curr.large.startsWith(
                    'https://lid.zoocdn.com/645/430/'
                )
                    ? curr.large.replace('https://lid.zoocdn.com/645/430', '')
                    : curr.large;
                return [
                    ...acc,
                    [curr.caption ?? null, src] as [string | null, string]
                ];
            }
            return acc;
        }, []);
        return {
            ...rest,
            gallery
        };
    });
}

/** Hack to fix Maybe<> and | undefined types in GQL response. Ideally this func
would check all properties and return "cleaned" objects */
function nonEmpty<T>(val: unknown): T {
    return val as T;
}

function extractSearchResultsMeta(
    data: ServerData['searchResults']
): SearchResultMeta {
    return {
        analyticsTaxonomy: data.analyticsTaxonomy,
        analyticsEcommerce: data.analyticsEcommerce,
        metaInfo: data.metaInfo,
        seoBlurb: data.seoBlurb,
        title: data.title
    };
}

interface GeoGraphqlError extends GraphQLError {
    extensions: {
        response: {
            body: {
                errors: {
                    validation_errors: {
                        current_value?: string | null;
                        field: string;
                    }[];
                }[];
            };
        };
    };
}

function isGeoGraphqlError(err: GraphQLError): err is GeoGraphqlError {
    const errors = err?.extensions?.['response']?.body?.errors;

    if (!Array.isArray(errors)) {
        return false;
    }

    const { validation_errors = [] } = errors[0];

    if (!Array.isArray(validation_errors)) {
        return false;
    }

    const { field } = validation_errors[0] || {};

    return field === 'geo_identifier';
}

export function getGeoError(error: unknown): GeoGraphqlError | undefined {
    if (error instanceof ApolloError) {
        const { graphQLErrors = [] } = error;

        return graphQLErrors.find<GeoGraphqlError>(isGeoGraphqlError);
    }
    return;
}

function pickError(error: unknown): string {
    if (
        typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        typeof error.message === 'string'
    ) {
        return error.message;
    }

    return '';
}

export function errorWithMessage(error: unknown): string {
    if (Array.isArray(error)) {
        return error.map(pickError).join('\n');
    }

    return pickError(error);
}

export function removeEverythingAfterHash(path?: string) {
    if (!path) {
        return '';
    }

    return path.replace(/#.*/, '');
}

export function getQ(q?: string | string[]) {
    if (!q) return;
    return Array.isArray(q) ? q[0] : q;
}
